"use client";

import { useState } from "react";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { writeToClipboard } from "@/lib/clipboard";
import { tPlural } from "@/lib/i18n/interpolate";

function fisherYatesShuffle(items: string[]): string[] {
  const result = items.slice();
  for (let i = result.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
}

function shuffleText(text: string, skipEmpty: boolean): string {
  let lines = text.split("\n");
  if (skipEmpty) {
    lines = lines.filter((line) => line.trim().length > 0);
  }
  return fisherYatesShuffle(lines).join("\n");
}

/** English strings. A translated content file overrides these through `ui`. */
export const SHUFFLE_LINES_STRINGS = {
  options: "Options",
  skipEmptyLines: "Skip empty lines",
  inputOnePerLine: "Input — one item per line",
  placeholder: "Alice\nBob\nCharlie\nDana",
  linesCount: "one:{count} line|other:{count} lines",
  shuffleAgain: "Shuffle Again",
  shuffle: "Shuffle",
  copied: "Copied!",
  copyResult: "Copy Result",
  clear: "Clear",
  shuffledOutput: "Shuffled output",
  inputLines: "Input lines",
  shuffledLines: "Shuffled lines",
} as const;

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4 text-center">
      <p className="font-semibold text-2xl text-foreground">{value}</p>
      <p className="mt-1 text-muted-foreground text-sm">{label}</p>
    </div>
  );
}

export default function ShuffleLinesWidget() {
  const strings = useToolUi(SHUFFLE_LINES_STRINGS);
  const fmt = useToolNumber();
  const locale = useToolLocale();
  const [input, setInput] = useState("");
  const [skipEmpty, setSkipEmpty] = useState(true);
  const [output, setOutput] = useState("");
  const [copied, setCopied] = useState(false);

  const lineCount = input.trim().length === 0 ? 0 : input.split("\n").length;
  const outputLineCount =
    output.trim().length === 0 ? 0 : output.split("\n").length;

  function handleShuffle() {
    if (input.trim().length === 0) {
      return;
    }
    setOutput(shuffleText(input, skipEmpty));
    setCopied(false);
  }

  function handleCopy() {
    if (output.length === 0) {
      return;
    }
    writeToClipboard(output).then((copied) => {
      if (!copied) {
        return;
      }

      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  function handleClear() {
    setInput("");
    setOutput("");
    setCopied(false);
  }

  return (
    <>
      {/* Options */}
      <section className="space-y-3">
        <p className="font-medium text-foreground text-sm">{strings.options}</p>
        <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm transition-colors has-[:checked]:border-primary has-[:checked]:bg-primary/5">
          <input
            checked={skipEmpty}
            className="accent-primary"
            onChange={(e) => setSkipEmpty(e.target.checked)}
            type="checkbox"
          />
          {strings.skipEmptyLines}
        </label>
      </section>

      {/* Input */}
      <div className="space-y-2">
        <label
          className="font-medium text-foreground text-sm"
          htmlFor="shuffle-input"
        >
          {strings.inputOnePerLine}
        </label>
        <textarea
          className="min-h-[200px] w-full resize-y rounded-lg border border-border bg-card p-4 font-mono text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          id="shuffle-input"
          onChange={(e) => setInput(e.target.value)}
          placeholder={strings.placeholder}
          value={input}
        />
        <p className="text-muted-foreground text-xs">
          {tPlural(strings.linesCount, locale, lineCount, {
            count: fmt(lineCount),
          })}
        </p>
      </div>

      {/* Actions */}
      <div className="flex flex-wrap gap-3">
        <button
          className="rounded-lg bg-primary px-5 py-2 font-medium text-primary-foreground text-sm transition-opacity hover:opacity-90 disabled:opacity-40"
          disabled={input.trim().length === 0}
          onClick={handleShuffle}
          type="button"
        >
          {output.length > 0 ? strings.shuffleAgain : strings.shuffle}
        </button>
        <button
          className="rounded-lg border border-border bg-card px-5 py-2 font-medium text-foreground text-sm transition-colors hover:bg-muted disabled:opacity-40"
          disabled={output.length === 0}
          onClick={handleCopy}
          type="button"
        >
          {copied ? strings.copied : strings.copyResult}
        </button>
        <button
          className="rounded-lg border border-border bg-card px-5 py-2 font-medium text-foreground text-sm transition-colors hover:bg-muted disabled:opacity-40"
          disabled={input.length === 0 && output.length === 0}
          onClick={handleClear}
          type="button"
        >
          {strings.clear}
        </button>
      </div>

      {/* Output */}
      {output.length > 0 && (
        <div className="space-y-2">
          <label
            className="font-medium text-foreground text-sm"
            htmlFor="shuffle-output"
          >
            {strings.shuffledOutput}
          </label>
          <textarea
            className="min-h-[200px] w-full resize-y rounded-lg border border-border bg-card p-4 font-mono text-foreground text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-primary"
            id="shuffle-output"
            readOnly
            value={output}
          />
          <p className="text-muted-foreground text-xs">
            {tPlural(strings.linesCount, locale, outputLineCount, {
              count: fmt(outputLineCount),
            })}
          </p>
        </div>
      )}

      {/* Stats */}
      {output.length > 0 && (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-2">
          <StatCard label={strings.inputLines} value={fmt(lineCount)} />
          <StatCard
            label={strings.shuffledLines}
            value={fmt(outputLineCount)}
          />
        </div>
      )}
    </>
  );
}
